/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

/* eslint-disable no-underscore-dangle */
import {
  FormControl,
  Grid,
  Box,
  IconButton,
  Alert,
  Autocomplete,
  RadioGroup,
  FormControlLabel,
  Tooltip,
  FormLabel,
} from '@mui/material';
import { selectHotelById, selectHotelUsers } from 'modules/hotels/selectors';
import ERROR_TYPES from 'modules/common/constants/error-types';
import TextField from '@mui/material/TextField';
import { Modal } from 'modules/common/components';
import { notificationActions } from 'modules/common/notifications/slice';
import { selectNotification } from 'modules/common/notifications/selectors';
import { Save, X } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import { hotelActions } from 'modules/hotels/slice';
import { selectOrganizationId } from 'modules/common/auth/selectors';
import { Formik } from 'formik';
import { format } from 'date-fns';
import { Typography } from './style';
import PAGINATION from '../../../common/constants/pagination-limits';
import { isEmpty } from '../../../common/helpers/object';
import { DATE_TIME_PATTERN } from '../../../common/constants/date-range';
import { THEMES } from '../../constants';

const FormStyles = { borderRadius: 10 };
//
const UserDataDrawerView = ({ show, isShow, userId }) => {
  //
  const dispatch = useDispatch();
  const notification = useSelector(selectNotification);
  const organizationId = useSelector(selectOrganizationId);
  const hotel = useSelector((state) => selectHotelById(state, userId));
  const hotelUsers = useSelector(selectHotelUsers);
  //
  const [isUpdate, setUpdate] = useState(false);
  const [initialValues, setInitialValues] = useState({
    id: '',
    name: '',
    roomCapacity: '',
    latestBookingDate: '',
    pmsStartDate: '',
    databricksHotelId: '',
    displayName: '',
    timezone: '',
    createdAt: '',
    updatedAt: '',
    chainName: '',
    city: '',
    state: '',
    country: '',
    // hotelType: '',
    chainId: '',
    hotelCode: '',
    ibe: '',
    interface: '',
    phram: '',
    phrdnName: '',
    pms: '',
    postalCode: '',
    propertyLatitude: '',
    propertyLongitude: '',
    propertyLocation: '',
    localCurrency: ' ',
    users: [],
    theme: '',
    collection: '',
  });
  const formElement = useRef(null);
  //
  useEffect(() => {
    if (notification?.isEnabled && notification?.type === ERROR_TYPES.SUCCESS) {
      if (notification?.isEnabled) dispatch(notificationActions.resetNotification());
      isShow(false);
    }
  }, [notification]);
  //
  const onSubmitUpdateHotel = async (values) => {
    const valueObj = {
      currencyUpdate: true,
      localCurrency: values?.localCurrency,
      theme: values?.theme,
    };
    dispatch(hotelActions.updateHotel({ ...valueObj, organizationId, hotelId: values?.id }));
  };
  //
  useEffect(() => {
    if (!isEmpty(hotelUsers)) {
      setInitialValues({
        ...initialValues,
        users: hotelUsers
          ?.filter((filter) => filter?.username !== undefined)
          .map((filter) => filter.username),
      });
    }
  }, [hotelUsers]);
  //
  useEffect(() => {
    if (hotel) {
      setInitialValues({
        id: hotel?.id,
        name: hotel?.name,
        roomCapacity: hotel?.roomCapacity,
        latestBookingDate: hotel?.latestBookingDate
          ? format(new Date(hotel?.latestBookingDate), DATE_TIME_PATTERN)
          : '',
        pmsStartDate: hotel?.pmsStartDate
          ? format(new Date(hotel?.pmsStartDate), DATE_TIME_PATTERN)
          : '',
        databricksHotelId: hotel?.databricksHotelId,
        displayName: hotel?.displayName,
        timezone: hotel?.timezone,
        createdAt: hotel?.createdAt ? format(new Date(hotel?.createdAt), DATE_TIME_PATTERN) : '',
        updatedAt: hotel?.updatedAt ? format(new Date(hotel?.updatedAt), DATE_TIME_PATTERN) : '',
        reportingCurrency: hotel?.reportingCurrency,
        chainName: hotel?.chainName,
        city: hotel?.city,
        state: hotel?.state,
        country: hotel?.country,
        // hotelType: hotel?.hotelType,
        chainId: hotel?.chainId,
        hotelCode: hotel?.hotelCode,
        ibe: hotel?.ibe,
        interface: hotel?.interface,
        phram: hotel?.phram,
        phrdnName: hotel?.phrdnName,
        pms: hotel?.pms,
        postalCode: hotel?.postalCode,
        propertyLatitude: hotel?.propertyLatitude,
        propertyLongitude: hotel?.propertyLongitude,
        propertyLocation: hotel?.propertyLocation,
        localCurrency: hotel?.localCurrency ?? ' ',
        users: [],
        theme: hotel?.theme,
        collection: hotel?.collection,
      });
      const query = `limit=${PAGINATION.USERS_PER_PAGE_COUNT}`;
      dispatch(
        hotelActions.getHotelUserData({
          organizationId,
          hotelId: hotel?.id,
          query,
        })
      );
    }
  }, [hotel]);
  //
  return (
    <Box
      xs={12}
      md={4}
      ml={4}
      mt={2}
      component={Grid}
      container
      item
      display="flex"
      direction="column"
      sx={{
        backgroundColor: 'white',
        flexShrink: 0,
        display: show ? 'flex' : 'none',
      }}
    >
      <Formik initialValues={initialValues} enableReinitialize onSubmit={onSubmitUpdateHotel}>
        {({ dirty, handleBlur, handleChange, handleSubmit, handleReset, values, isSubmitting }) => (
          <form noValidate onSubmit={handleSubmit}>
            <Grid container direction="row" justifyContent="space-between" px={2} mb={6}>
              <Grid
                item
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  width: 250,
                }}
                alignSelf="center"
              >
                <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600 }}>
                  {hotel?.name}
                </Typography>
              </Grid>
              <Grid>
                <Tooltip title="Update">
                  <IconButton
                    disabled={isSubmitting}
                    onClick={() => setUpdate(true)}
                    color="inherit"
                    size="small"
                  >
                    <Save />
                  </IconButton>
                </Tooltip>
                <IconButton
                  color="inherit"
                  size="small"
                  onClick={() => {
                    if (dirty) {
                      handleReset();
                    }
                    isShow(false);
                  }}
                >
                  <X />
                </IconButton>
              </Grid>
            </Grid>
            <Grid container spacing={2} maxHeight={570} sx={{ overflow: 'auto' }}>
              {notification?.isEnabled && notification?.type === ERROR_TYPES.ERROR && (
                <Alert mt={2} mb={3} severity={notification?.type}>
                  {notification?.message}
                </Alert>
              )}
              <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    name="name"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.name}
                    label="Name"
                    variant="outlined"
                    size="large"
                    disabled
                  />
                </FormControl>
              </Grid>
              <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    name="id"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.displayName}
                    label="Display Name"
                    variant="outlined"
                    size="large"
                    disabled
                  />
                </FormControl>
              </Grid>
              <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    name="chainId"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.chainId}
                    label="Chain Id"
                    variant="outlined"
                    size="large"
                    disabled
                  />
                </FormControl>
              </Grid>
              <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    name="chainName"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.chainName}
                    label="Chain Name"
                    variant="outlined"
                    size="large"
                    disabled
                  />
                </FormControl>
              </Grid>
              <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    name="id"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.roomCapacity}
                    label="Room Capacity"
                    variant="outlined"
                    size="large"
                    disabled
                  />
                </FormControl>
              </Grid>
              <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    name="id"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.databricksHotelId}
                    label="Databricks Hotel Id"
                    variant="outlined"
                    size="large"
                    disabled
                  />
                </FormControl>
              </Grid>
              <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    disabled
                    size="small"
                    name="localCurrency"
                    value={values.localCurrency ?? ' '}
                    label="Local Currency"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                  />
                </FormControl>
              </Grid>
              <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    name="id"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.timezone}
                    label="Timezone"
                    variant="outlined"
                    size="large"
                    disabled
                  />
                </FormControl>
              </Grid>
              <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    name="id"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.latestBookingDate}
                    label="Latest Booking Date"
                    variant="outlined"
                    size="large"
                    disabled
                  />
                </FormControl>
              </Grid>
              <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    name="id"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.pmsStartDate}
                    label="PMS Start Date"
                    variant="outlined"
                    size="large"
                    disabled
                  />
                </FormControl>
              </Grid>
              <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    name="createdAt"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.createdAt}
                    label="Created At"
                    variant="outlined"
                    size="large"
                    disabled
                  />
                </FormControl>
              </Grid>
              <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    name="id"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.updatedAt}
                    label="Updated At"
                    variant="outlined"
                    size="large"
                    disabled
                  />
                </FormControl>
              </Grid>
              <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    name="currency"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.reportingCurrency}
                    label="Currency"
                    variant="outlined"
                    size="large"
                    InputLabelProps={{ shrink: true }}
                    disabled
                  />
                </FormControl>
              </Grid>
              <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    name="propertyLocation"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.propertyLocation}
                    label="Street Address"
                    variant="outlined"
                    size="large"
                    disabled
                  />
                </FormControl>
              </Grid>
              <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    name="city"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.city}
                    label="City"
                    variant="outlined"
                    size="large"
                    disabled
                  />
                </FormControl>
              </Grid>
              <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    name="state"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.state}
                    label="State"
                    variant="outlined"
                    size="large"
                    disabled
                  />
                </FormControl>
              </Grid>
              <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    name="postalCode"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.postalCode}
                    label="Postal Code"
                    variant="outlined"
                    size="large"
                    disabled
                  />
                </FormControl>
              </Grid>
              <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    name="country"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.country}
                    label="Country"
                    variant="outlined"
                    size="large"
                    disabled
                  />
                </FormControl>
              </Grid>
              {/* <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    name="hotelType"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.hotelType}
                    label="Hotel Type"
                    variant="outlined"
                    size="large"
                    disabled
                  />
                </FormControl>
              </Grid> */}
              <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    name="hotelCode"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.hotelCode}
                    label="Hotel Code"
                    variant="outlined"
                    size="large"
                    disabled
                  />
                </FormControl>
              </Grid>
              <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    name="ibe"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.ibe}
                    label="IBE"
                    variant="outlined"
                    size="large"
                    disabled
                  />
                </FormControl>
              </Grid>
              <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    name="interface"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.interface}
                    label="Interface"
                    variant="outlined"
                    size="large"
                    disabled
                  />
                </FormControl>
              </Grid>
              <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    name="phram"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.phram}
                    label="PHRAM"
                    variant="outlined"
                    size="large"
                    disabled
                  />
                </FormControl>
              </Grid>
              <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    name="phrdnName"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.phrdnName}
                    label="PHRDN Name"
                    variant="outlined"
                    size="large"
                    disabled
                  />
                </FormControl>
              </Grid>
              <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    name="pms"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.pms}
                    label="PMS"
                    variant="outlined"
                    size="large"
                    disabled
                  />
                </FormControl>
              </Grid>
              <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    name="propertyLatitude"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.propertyLatitude}
                    label="Property Latitude"
                    variant="outlined"
                    size="large"
                    disabled
                  />
                </FormControl>
              </Grid>
              <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    name="propertyLongitude"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.propertyLongitude}
                    label="Property Longitude"
                    variant="outlined"
                    size="large"
                    disabled
                  />
                </FormControl>
              </Grid>
              <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                <FormControl sx={FormStyles} fullWidth>
                  <TextField
                    size="small"
                    name="collection"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.collection}
                    label="Collection"
                    variant="outlined"
                    disabled
                  />
                </FormControl>
              </Grid>
              <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                <FormControl sx={FormStyles} fullWidth>
                  <Autocomplete
                    multiple
                    limitTags={2}
                    options={values.users ?? []}
                    value={values.users}
                    open={false}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Users"
                        InputProps={{ ...params.InputProps, readOnly: true }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            height: 'auto',
                            overflow: 'true',
                          },
                          '& .MuiChip-deleteIcon': { display: 'none' },
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} style={{ pointerEvents: 'none' }}>
                        {option}
                      </li>
                    )}
                    disableClearable
                    readOnly
                  />
                </FormControl>
              </Grid>
              <Grid container justifyContent="space-around" alignItems="center" px={4} my={2}>
                <FormControl sx={FormStyles} fullWidth>
                  <FormLabel>Theme</FormLabel>
                  <RadioGroup
                    row
                    name="theme"
                    value={values.theme}
                    onChange={handleChange}
                    sx={{ marginTop: 2, marginLeft: 4 }}
                  >
                    {THEMES.map((theme) => (
                      <Tooltip key={theme.name} title={theme.label}>
                        <FormControlLabel
                          value={theme.name}
                          control={
                            <input
                              type="radio"
                              name="theme"
                              value={theme.name}
                              checked={values.theme === theme.name}
                              onChange={handleChange}
                              style={{ display: 'none' }}
                            />
                          }
                          label={
                            <div
                              style={{
                                width: 40,
                                height: 40,
                                backgroundColor: theme.color,
                                borderRadius: '50%',
                                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                                cursor: 'pointer',
                                border:
                                  values.theme === theme.name
                                    ? '1px solid white'
                                    : '2px solid transparent',
                                outline:
                                  values.theme === theme.name ? `2px solid ${theme.color}` : 'none',
                                marginRight: 12,
                              }}
                            />
                          }
                        />
                      </Tooltip>
                    ))}
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Modal
                open={isUpdate}
                handleClose={() => {
                  if (dirty) {
                    handleReset();
                  }
                  setUpdate(false);
                }}
                title="Update Hotel"
                content="Are you sure you want to update the hotel ?"
                handleSuccess={() => {
                  formElement.current?.click();
                  setUpdate(false);
                }}
                closeLabel="Cancel"
                successLabel="Update"
                variant="contained"
                color="primary"
              />
            </Grid>
            <input ref={formElement} style={{ visibility: 'hidden' }} type="submit" />
          </form>
        )}
      </Formik>
    </Box>
  );
};
//
export default UserDataDrawerView;
